import { ListMusic, CheckCircle, Clock, Zap, Filter } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import type { QueueMetrics } from '../../types/analytics';
import { PEOPLES_PICK_HIDDEN } from '../../utils/featureFlags';

interface Props {
  queue: QueueMetrics;
}

function formatMs(ms: number): string {
  if (ms === 0) return 'N/A';
  const mins = Math.floor(ms / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

export default function QueueHealth({ queue }: Props) {
  const statusData = [
    { name: 'Played', value: queue.totalPlayed, color: '#10B981' },
    { name: 'Queued', value: queue.totalQueued, color: '#6366F1' },
    { name: 'Pending', value: queue.totalPending, color: '#F59E0B' },
    { name: 'Rejected', value: queue.totalRejected, color: '#EF4444' },
  ];

  const total = queue.totalPlayed + queue.totalQueued + queue.totalPending + queue.totalRejected;

  if (total === 0) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <ListMusic className="w-5 h-5 text-primary" />
          Queue Health
        </h3>
        <div className="h-32 flex items-center justify-center text-gray-500">
          <p>No queue data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        <ListMusic className="w-5 h-5 text-primary" />
        Queue Health
      </h3>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Status Breakdown Chart */}
        <div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={statusData} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
              <XAxis type="number" tick={{ fontSize: 11, fill: '#9CA3AF' }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#9CA3AF' }} width={70} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#F3F4F6',
                  fontSize: 12,
                }}
                formatter={(value?: number) => [`${value ?? 0} songs`]}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {statusData.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Queue Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white dark:bg-gray-900 rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 mb-1">
              <CheckCircle className="w-3.5 h-3.5 text-green-500" />
              Success Rate
            </div>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{queue.requestSuccessRate}%</p>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 mb-1">
              <Clock className="w-3.5 h-3.5 text-blue-500" />
              Avg Time to Queue
            </div>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{formatMs(queue.avgTimeToQueueMs)}</p>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 mb-1">
              <Clock className="w-3.5 h-3.5 text-purple-500" />
              Avg Time to Play
            </div>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{formatMs(queue.avgTimeToPlayMs)}</p>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 mb-1">
              <Filter className="w-3.5 h-3.5 text-amber-500" />
              Content Filtered
            </div>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{queue.contentFilteredCount}</p>
          </div>
          {!PEOPLES_PICK_HIDDEN && queue.superRequestCount > 0 && (
            <div className="bg-white dark:bg-gray-900 rounded-lg p-3 col-span-2">
              <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 mb-1">
                <Zap className="w-3.5 h-3.5 text-yellow-500" />
                People's Pick Requests
              </div>
              <p className="text-xl font-bold text-gray-900 dark:text-white">{queue.superRequestCount}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
